'use client'

import Link from 'next/link'
import HeroVideoMock from '@/components/landings/santa-hero-video-mock'
import type { SantaTrackerCta } from '@/lib/product-analytics'
import type { TrackerCopy, TrackerPhase } from '@/lib/santa-tracker'

/**
 * Puente hacia el producto. Va al final, después de que la experiencia haya
 * cumplido lo que prometía: es la continuación natural («mientras esperas su
 * llegada…»), no un anuncio interpuesto. En Nochebuena cambia el tono, porque
 * ese día Papá Noel está ocupado y fingir lo contrario chirría.
 *
 * Lleva el **mismo simulador de llamada que el hero de la home**
 * (`HeroVideoMock`), y no una copia: el bloque tiene que prometer exactamente lo
 * que el visitante se va a encontrar al otro lado, y el original ya trae el
 * diferido de carga (no compite con el primer render), el póster WebP de
 * respaldo y el respeto a `prefers-reduced-motion`.
 */
export function TrackerCtaSection({
  copy,
  phase,
  authHref,
  charactersHref,
  freeSantaHref,
  onCtaClick,
}: {
  copy: TrackerCopy
  phase: TrackerPhase
  authHref: string
  charactersHref: string
  /** La landing «Papá Noel gratis» del locale. */
  freeSantaHref: string
  onCtaClick: (cta: SantaTrackerCta) => void
}) {
  return (
    <section
      aria-labelledby="tracker-cta-heading"
      className="overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-6 shadow-lg md:p-8"
    >
      <div className="grid items-center gap-8 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        {/* En móvil el simulador va primero: es lo que explica de un vistazo de
            qué se está hablando, antes de leer una línea. */}
        <div className="mx-auto w-full max-w-[340px] md:max-w-[420px]">
          <HeroVideoMock />
        </div>

        <div className="flex flex-col gap-4 text-center md:text-left">
          <h2 id="tracker-cta-heading" className="font-christmas text-2xl font-semibold md:text-3xl">
            {copy.cta.heading}
          </h2>
          <p className="text-base text-white/80 md:text-lg">{copy.cta.body}</p>
          {phase === 'traveling' && (
            <p className="text-sm text-amber-100/90">{copy.cta.travelingNote}</p>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center md:justify-start">
            <Link
              href={authHref}
              onClick={() => onCtaClick('video_call')}
              className="font-christmas whitespace-nowrap rounded-full bg-secondary px-7 py-4 text-center text-lg font-semibold text-secondary-foreground transition-transform hover:scale-105"
            >
              {copy.cta.primary}
            </Link>
            <Link
              href={charactersHref}
              onClick={() => onCtaClick('characters')}
              className="font-christmas whitespace-nowrap rounded-full border border-white/60 bg-white/10 px-7 py-4 text-center text-lg font-semibold text-white transition-transform hover:scale-105 hover:bg-white/20"
            >
              {copy.cta.secondary}
            </Link>
          </div>
          <Link
            href={freeSantaHref}
            onClick={() => onCtaClick('free_santa')}
            className="text-base font-semibold text-white underline decoration-2 underline-offset-4 hover:text-amber-100"
          >
            {copy.cta.free}
          </Link>
        </div>
      </div>
    </section>
  )
}
